import React, { useState } from 'react';
import {
    Button,
    Group,
    NumberInput,
    Select,
    Stack,
    Table,
    Text,
    ActionIcon,
    Box,
    Title,
    Tooltip,
    ThemeIcon,
    Badge,
    Center,
    rem,
} from '@mantine/core';
import { IconPlus, IconTrash, IconChecklist, IconRepeat, IconInbox, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IQuestionnaires } from '../../../global/entities/Questionnaires/Questionnaires';
import {
    CarePlanSchedulingPhase,
    ICarePlanTaskInput,
} from '../../../global/entities/CarePlanTemplate/CarePlanTemplate';

const RECURRENCE_TYPE_OPTIONS = [
    { value: 'DAILY_AM', labelKey: 'formTask.dailyAm', fallback: 'Daily (am)' },
    { value: 'DAILY_PM', labelKey: 'formTask.dailyPm', fallback: 'Daily (pm)' },
    { value: 'DAILY_NIGHT', labelKey: 'formTask.dailyNight', fallback: 'Daily (night)' },
    { value: 'WEEKLY', labelKey: 'formTask.weekly', fallback: 'Weekly' },
    { value: 'MONTHLY', labelKey: 'formTask.monthly', fallback: 'Monthly' },
];

const PHASE_OPTIONS: { value: CarePlanSchedulingPhase; labelKey: string; fallback: string }[] = [
    { value: 'PostConsent', labelKey: 'carePlan.phase.postConsent', fallback: 'After consent' },
    { value: 'EventRelativePre', labelKey: 'carePlan.phase.eventRelativePre', fallback: 'Before event' },
    { value: 'EventRelativePost', labelKey: 'carePlan.phase.eventRelativePost', fallback: 'After event' },
];

const hasInterval = (type: string | null) => type === 'WEEKLY' || type === 'MONTHLY';

const SECTION_CARD_STYLE: React.CSSProperties = {
    border: '1px solid var(--mantine-color-gray-2)',
    borderRadius: 'var(--mantine-radius-lg)',
    backgroundColor: 'var(--mantine-color-white)',
    padding: 'var(--mantine-spacing-lg)',
};

const COMPOSER_STYLE: React.CSSProperties = {
    border: '1px solid var(--mantine-color-gray-2)',
    borderRadius: 'var(--mantine-radius-md)',
    backgroundColor: 'var(--mantine-color-gray-0)',
    padding: 'var(--mantine-spacing-md)',
};

const ColumnHead: React.FC<{ children?: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
    <Table.Th style={{ textAlign: align }}>
        <Text component="span" fz="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.04em' }}>
            {children}
        </Text>
    </Table.Th>
);

export type ICarePlanTasksConfigProps = {
    value?: ICarePlanTaskInput[];
    onChange: (tasks: ICarePlanTaskInput[]) => void;
    questionnairesData?: IQuestionnaires[];
};

const CarePlanTasksConfig: React.FC<ICarePlanTasksConfigProps> = ({
    value = [],
    onChange,
    questionnairesData = [],
}) => {
    const { t } = useTranslation();
    const recurrenceOptions = RECURRENCE_TYPE_OPTIONS.map(o => ({ value: o.value, label: t(o.labelKey, o.fallback) }));
    const phaseOptions = PHASE_OPTIONS.map(o => ({ value: o.value, label: t(o.labelKey, o.fallback) }));

    // One-time task add form
    const [oneTimeQuestionnaireId, setOneTimeQuestionnaireId] = useState<string | null>(null);
    const [oneTimePhase, setOneTimePhase] = useState<CarePlanSchedulingPhase>('PostConsent');
    const [oneTimeAvailable, setOneTimeAvailable] = useState<number>(0);
    const [oneTimeDue, setOneTimeDue] = useState<number>(7);

    // Recurring task add form
    const [recQuestionnaireId, setRecQuestionnaireId] = useState<string | null>(null);
    const [recPhase, setRecPhase] = useState<CarePlanSchedulingPhase>('PostConsent');
    const [recAvailable, setRecAvailable] = useState<number>(0);
    const [recDue, setRecDue] = useState<number>(7);
    const [recType, setRecType] = useState<string | null>('WEEKLY');
    const [recInterval, setRecInterval] = useState<number>(1);
    const [recEnd, setRecEnd] = useState<number | ''>('');
    const [recExpiration, setRecExpiration] = useState<number | ''>('');

    const questionnaireSelectData = questionnairesData.map(q => ({ value: q.id.toString(), label: q.name }));

    const parseQuestionnaireId = (raw: string | null): number | null => {
        if (!raw) return null;
        const parsed = parseInt(raw);
        return isNaN(parsed) || parsed <= 0 ? null : parsed;
    };

    const handleAddOneTime = () => {
        const questionnaireID = parseQuestionnaireId(oneTimeQuestionnaireId);
        if (questionnaireID === null) return;

        const newTask: ICarePlanTaskInput = {
            questionnaireID,
            schedulingPhase: oneTimePhase,
            availableDateOffsetDays: oneTimeAvailable,
            dueDateOffsetDays: oneTimeDue,
            isRecurring: false,
            recurrenceType: null,
            recurrenceInterval: 1,
            recurrenceEndOffsetDays: null,
            expirationDays: null,
        };

        onChange([...value, newTask]);
        setOneTimeQuestionnaireId(null);
        setOneTimePhase('PostConsent');
        setOneTimeAvailable(0);
        setOneTimeDue(7);
    };

    const handleAddRecurring = () => {
        const questionnaireID = parseQuestionnaireId(recQuestionnaireId);
        if (questionnaireID === null) return;

        const newTask: ICarePlanTaskInput = {
            questionnaireID,
            schedulingPhase: recPhase,
            availableDateOffsetDays: recAvailable,
            dueDateOffsetDays: recDue,
            isRecurring: true,
            recurrenceType: recType,
            recurrenceInterval: recInterval,
            recurrenceEndOffsetDays: recEnd !== '' ? recEnd : null,
            expirationDays: recExpiration !== '' ? recExpiration : null,
        };

        onChange([...value, newTask]);
        setRecQuestionnaireId(null);
        setRecPhase('PostConsent');
        setRecAvailable(0);
        setRecDue(7);
        setRecType('WEEKLY');
        setRecInterval(1);
        setRecEnd('');
        setRecExpiration('');
    };

    const handleRemove = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const handleUpdate = (
        index: number,
        field: keyof ICarePlanTaskInput,
        newValue: number | boolean | string | null,
    ) => {
        const updated = [...value];
        updated[index] = { ...updated[index], [field]: newValue };
        onChange(updated);
    };

    // The Interval field is hidden (and not submitted) for DAILY_* types, but its stored
    // value isn't otherwise scoped to WEEKLY/MONTHLY — switching away from those without
    // resetting it would silently carry over a stale interval into a daily template, which
    // RecurrenceService does apply when advancing/catching up DAILY_* occurrences.
    const handleRecurrenceTypeChange = (index: number, recurrenceType: string | null) => {
        const updated = [...value];
        updated[index] = {
            ...updated[index],
            recurrenceType,
            recurrenceInterval: hasInterval(recurrenceType) ? updated[index].recurrenceInterval : 1,
        };
        onChange(updated);
    };

    const getQuestionnaireName = (questionnaireId: number) => {
        return questionnairesData.find(q => q.id === questionnaireId)?.name || '';
    };

    const indexedTasks = value.map((task, index) => ({ task, index }));
    const oneTimeTasks = indexedTasks.filter(({ task }) => !task.isRecurring);
    const recurringTasks = indexedTasks.filter(({ task }) => task.isRecurring);

    // Label text with an info icon beside it; hovering the icon (not the whole
    // field) shows the tooltip.
    const renderLabelWithTooltip = (label: string, tooltip: string) => (
        <Box component="span" style={{ display: 'inline-flex', alignItems: 'center', gap: rem(4) }}>
            <Text component="span" fz="sm" fw={600}>
                {label}
            </Text>
            <Tooltip label={tooltip} multiline w={250}>
                <IconInfoCircle size={14} style={{ cursor: 'pointer', color: 'var(--mantine-color-gray-5)' }} />
            </Tooltip>
        </Box>
    );

    const renderEmptyState = (message: string) => (
        <Center py="xl">
            <Stack align="center" gap="xs">
                <ThemeIcon variant="light" color="gray" size={44} radius="xl">
                    <IconInbox size={22} />
                </ThemeIcon>
                <Text c="dimmed" size="sm">
                    {message}
                </Text>
            </Stack>
        </Center>
    );

    return (
        <Stack gap="lg">
            <Stack gap="xs">
                <Title order={5} fw={600}>
                    {t('carePlan.tasks.title', 'Care plan tasks')}
                </Title>
                <Text size="sm" c="dimmed">
                    {t('carePlan.tasks.description', 'Configure the questionnaires scheduled by this care plan. Offsets may be negative for tasks scheduled before the event.')}
                </Text>
            </Stack>

            {/* One-time tasks */}
            <Box style={SECTION_CARD_STYLE}>
                <Stack gap="md">
                    <Group justify="space-between" align="center" wrap="nowrap">
                        <Group gap="sm" align="center" wrap="nowrap">
                            <ThemeIcon variant="light" color="blue" size={38} radius="md">
                                <IconChecklist size={20} />
                            </ThemeIcon>
                            <Title order={6} fw={600}>
                                {t('carePlan.tasks.oneTimeSection', 'One-time tasks')}
                            </Title>
                        </Group>
                        {oneTimeTasks.length > 0 && (
                            <Badge variant="light" color="blue" radius="sm" size="lg">
                                {oneTimeTasks.length}
                            </Badge>
                        )}
                    </Group>

                    <Box style={COMPOSER_STYLE}>
                        <Group align="flex-end" gap="md" wrap="wrap">
                            <Box style={{ flex: 1, minWidth: rem(220) }}>
                                <Select
                                    label={t('carePlan.tasks.questionnaire', 'Questionnaire')}
                                    placeholder={t('formHcf.selectQuestionnaire')}
                                    data={questionnaireSelectData}
                                    value={oneTimeQuestionnaireId}
                                    onChange={setOneTimeQuestionnaireId}
                                    styles={{ dropdown: { minWidth: 600 } }}
                                    comboboxProps={{ middlewares: { flip: false, shift: false }, position: 'bottom-start' }}
                                    searchable
                                    clearable
                                />
                            </Box>
                            <Select
                                label={t('carePlan.tasks.phase', 'Scheduling phase')}
                                data={phaseOptions}
                                value={oneTimePhase}
                                onChange={(val) => setOneTimePhase((val as CarePlanSchedulingPhase) ?? 'PostConsent')}
                                w={rem(170)}
                            />
                            <NumberInput
                                label={t('carePlan.tasks.availableOffsetDays', 'Available (days)')}
                                value={oneTimeAvailable}
                                onChange={(val) => setOneTimeAvailable(typeof val === 'number' ? val : 0)}
                                w={rem(120)}
                            />
                            <NumberInput
                                label={t('carePlan.tasks.dueOffsetDays', 'Due (days)')}
                                value={oneTimeDue}
                                onChange={(val) => setOneTimeDue(typeof val === 'number' ? val : 7)}
                                w={rem(120)}
                            />
                            <Button
                                leftSection={<IconPlus size={16} />}
                                onClick={handleAddOneTime}
                                disabled={!oneTimeQuestionnaireId}
                            >
                                {t('button.add')}
                            </Button>
                        </Group>
                    </Box>

                    {oneTimeTasks.length > 0 ? (
                        <Table.ScrollContainer minWidth={680}>
                            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <ColumnHead>{t('carePlan.tasks.questionnaire', 'Questionnaire')}</ColumnHead>
                                        <ColumnHead>{t('carePlan.tasks.phase', 'Scheduling phase')}</ColumnHead>
                                        <ColumnHead>{t('carePlan.tasks.availableOffsetDays', 'Available (days)')}</ColumnHead>
                                        <ColumnHead>{t('carePlan.tasks.dueOffsetDays', 'Due (days)')}</ColumnHead>
                                        <ColumnHead align="right"> </ColumnHead>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {oneTimeTasks.map(({ task, index }) => (
                                        <Table.Tr key={index}>
                                            <Table.Td>
                                                <Text fz="sm" fw={500}>
                                                    {getQuestionnaireName(task.questionnaireID)}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Select
                                                    data={phaseOptions}
                                                    value={task.schedulingPhase}
                                                    onChange={(val) => handleUpdate(index, 'schedulingPhase', (val as CarePlanSchedulingPhase) ?? 'PostConsent')}
                                                    w={rem(160)}
                                                    size="sm"
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberInput
                                                    value={task.availableDateOffsetDays}
                                                    onChange={(val) => handleUpdate(index, 'availableDateOffsetDays', typeof val === 'number' ? val : 0)}
                                                    w={rem(100)}
                                                    size="sm"
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberInput
                                                    value={task.dueDateOffsetDays}
                                                    onChange={(val) => handleUpdate(index, 'dueDateOffsetDays', typeof val === 'number' ? val : 7)}
                                                    w={rem(100)}
                                                    size="sm"
                                                />
                                            </Table.Td>
                                            <Table.Td style={{ textAlign: 'right' }}>
                                                <Tooltip label={t('button.delete', 'Delete')}>
                                                    <ActionIcon color="red" variant="subtle" onClick={() => handleRemove(index)}>
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    ) : (
                        renderEmptyState(t('carePlan.tasks.noOneTimeTasks', 'No one-time tasks yet.'))
                    )}
                </Stack>
            </Box>

            {/* Recurring tasks */}
            <Box style={SECTION_CARD_STYLE}>
                <Stack gap="md">
                    <Group justify="space-between" align="center" wrap="nowrap">
                        <Group gap="sm" align="center" wrap="nowrap">
                            <ThemeIcon variant="light" color="grape" size={38} radius="md">
                                <IconRepeat size={20} />
                            </ThemeIcon>
                            <Title order={6} fw={600}>
                                {t('carePlan.tasks.recurringSection', 'Recurring tasks')}
                            </Title>
                        </Group>
                        {recurringTasks.length > 0 && (
                            <Badge variant="light" color="grape" radius="sm" size="lg">
                                {recurringTasks.length}
                            </Badge>
                        )}
                    </Group>

                    <Box style={COMPOSER_STYLE}>
                        <Group align="flex-end" gap="md" wrap="wrap">
                            <Box style={{ flex: 1, minWidth: rem(200) }}>
                                <Select
                                    label={t('carePlan.tasks.questionnaire', 'Questionnaire')}
                                    placeholder={t('formHcf.selectQuestionnaire')}
                                    data={questionnaireSelectData}
                                    value={recQuestionnaireId}
                                    onChange={setRecQuestionnaireId}
                                    styles={{ dropdown: { minWidth: 600 } }}
                                    comboboxProps={{ middlewares: { flip: false, shift: false }, position: 'bottom-start' }}
                                    searchable
                                    clearable
                                />
                            </Box>
                            <Select
                                label={t('carePlan.tasks.phase', 'Scheduling phase')}
                                data={phaseOptions}
                                value={recPhase}
                                onChange={(val) => setRecPhase((val as CarePlanSchedulingPhase) ?? 'PostConsent')}
                                w={rem(170)}
                            />
                            <NumberInput
                                label={t('carePlan.tasks.firstAvailableOffsetDays', 'First available (days)')}
                                value={recAvailable}
                                onChange={(val) => setRecAvailable(typeof val === 'number' ? val : 0)}
                                w={rem(130)}
                            />
                            <NumberInput
                                label={t('carePlan.tasks.firstDueOffsetDays', 'First due (days)')}
                                value={recDue}
                                onChange={(val) => setRecDue(typeof val === 'number' ? val : 7)}
                                w={rem(130)}
                            />
                            <Select
                                label={t('carePlan.tasks.recurrenceType', 'Recurrence')}
                                data={recurrenceOptions}
                                value={recType}
                                onChange={(val) => {
                                    setRecType(val);
                                    if (!hasInterval(val)) setRecInterval(1);
                                }}
                                w={rem(150)}
                            />
                            {hasInterval(recType) && (
                                <NumberInput
                                    label={t('carePlan.tasks.recurrenceInterval', 'Interval')}
                                    value={recInterval}
                                    onChange={(val) => setRecInterval(typeof val === 'number' ? val : 1)}
                                    min={1}
                                    max={12}
                                    w={rem(96)}
                                />
                            )}
                            <NumberInput
                                label={renderLabelWithTooltip(
                                    t('carePlan.tasks.recurrenceEndOffsetDays', 'Stop (days)'),
                                    t('carePlan.tasks.recurrenceEndOffsetDaysTooltip'),
                                )}
                                value={recEnd}
                                onChange={(val) => setRecEnd(typeof val === 'number' ? val : '')}
                                w={rem(120)}
                                placeholder="-"
                            />
                            <NumberInput
                                label={renderLabelWithTooltip(
                                    t('carePlan.tasks.expirationDays', 'Expire (days)'),
                                    t('carePlan.tasks.expirationDaysTooltip'),
                                )}
                                value={recExpiration}
                                onChange={(val) => setRecExpiration(typeof val === 'number' ? val : '')}
                                min={1}
                                w={rem(120)}
                                placeholder="-"
                            />
                            <Button
                                leftSection={<IconPlus size={16} />}
                                onClick={handleAddRecurring}
                                disabled={!recQuestionnaireId}
                            >
                                {t('button.add')}
                            </Button>
                        </Group>
                    </Box>

                    {recurringTasks.length > 0 ? (
                        <Table.ScrollContainer minWidth={900}>
                            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <ColumnHead>{t('carePlan.tasks.questionnaire', 'Questionnaire')}</ColumnHead>
                                        <ColumnHead>{t('carePlan.tasks.phase', 'Scheduling phase')}</ColumnHead>
                                        <ColumnHead>{t('carePlan.tasks.firstAvailableOffsetDays', 'First available (days)')}</ColumnHead>
                                        <ColumnHead>{t('carePlan.tasks.firstDueOffsetDays', 'First due (days)')}</ColumnHead>
                                        <ColumnHead>{t('carePlan.tasks.recurrenceType', 'Recurrence')}</ColumnHead>
                                        <ColumnHead>{t('carePlan.tasks.recurrenceInterval', 'Interval')}</ColumnHead>
                                        <ColumnHead>{t('carePlan.tasks.recurrenceEndOffsetDays', 'Stop (days)')}</ColumnHead>
                                        <ColumnHead>{t('carePlan.tasks.expirationDays', 'Expire (days)')}</ColumnHead>
                                        <ColumnHead align="right"> </ColumnHead>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {recurringTasks.map(({ task, index }) => (
                                        <Table.Tr key={index}>
                                            <Table.Td>
                                                <Text fz="sm" fw={500}>
                                                    {getQuestionnaireName(task.questionnaireID)}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Select
                                                    data={phaseOptions}
                                                    value={task.schedulingPhase}
                                                    onChange={(val) => handleUpdate(index, 'schedulingPhase', (val as CarePlanSchedulingPhase) ?? 'PostConsent')}
                                                    w={rem(160)}
                                                    size="sm"
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberInput
                                                    value={task.availableDateOffsetDays}
                                                    onChange={(val) => handleUpdate(index, 'availableDateOffsetDays', typeof val === 'number' ? val : 0)}
                                                    w={rem(100)}
                                                    size="sm"
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberInput
                                                    value={task.dueDateOffsetDays}
                                                    onChange={(val) => handleUpdate(index, 'dueDateOffsetDays', typeof val === 'number' ? val : 7)}
                                                    w={rem(100)}
                                                    size="sm"
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <Select
                                                    aria-label={t('carePlan.tasks.recurrenceType', 'Recurrence')}
                                                    data={recurrenceOptions}
                                                    value={task.recurrenceType}
                                                    onChange={(val) => handleRecurrenceTypeChange(index, val)}
                                                    w={rem(150)}
                                                    size="sm"
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                {hasInterval(task.recurrenceType) ? (
                                                    <NumberInput
                                                        value={task.recurrenceInterval}
                                                        onChange={(val) => handleUpdate(index, 'recurrenceInterval', typeof val === 'number' ? val : 1)}
                                                        min={1}
                                                        max={12}
                                                        w={rem(90)}
                                                        size="sm"
                                                    />
                                                ) : (
                                                    <Text size="sm" c="dimmed">—</Text>
                                                )}
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberInput
                                                    value={task.recurrenceEndOffsetDays ?? ''}
                                                    onChange={(val) => handleUpdate(index, 'recurrenceEndOffsetDays', typeof val === 'number' ? val : null)}
                                                    w={rem(100)}
                                                    size="sm"
                                                    placeholder="-"
                                                />
                                            </Table.Td>
                                            <Table.Td>
                                                <NumberInput
                                                    aria-label={t('carePlan.tasks.expirationDays', 'Expire (days)')}
                                                    value={task.expirationDays ?? ''}
                                                    onChange={(val) => handleUpdate(index, 'expirationDays', typeof val === 'number' ? val : null)}
                                                    min={1}
                                                    w={rem(100)}
                                                    size="sm"
                                                    placeholder="-"
                                                />
                                            </Table.Td>
                                            <Table.Td style={{ textAlign: 'right' }}>
                                                <Tooltip label={t('button.delete', 'Delete')}>
                                                    <ActionIcon color="red" variant="subtle" onClick={() => handleRemove(index)}>
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    ) : (
                        renderEmptyState(t('carePlan.tasks.noRecurringTasks', 'No recurring tasks yet.'))
                    )}
                </Stack>
            </Box>
        </Stack>
    );
};

export default CarePlanTasksConfig;
